<script setup lang="ts">
import {ref, computed, onMounted, onUnmounted, watch, provide, inject} from 'vue'
import {useI18n} from 'vue-i18n'
import {useRoute, useRouter} from 'vue-router'
import Footer from '@/components/Footer.vue'
import {loadProductData} from '@/services/productService'
import type {ProductCategory} from '@/types/product'

const route = useRoute()
const router = useRouter()
const {t, locale} = useI18n({
  useScope: 'global',
  inheritLocale: true
})

const drawer = ref(false)
const productsMenuOpen = ref(true)
const windowWidth = ref(0)

// 全局产品分类状态
const globalProductCategories = ref<ProductCategory[]>([])
const selectedCategory = ref<string | null>(null)

// 加载产品分类
const loadCategories = async () => {
  try {
    globalProductCategories.value = await loadProductData()
    // 默认选择第一个分类
    if (globalProductCategories.value.length > 0) {
      selectedCategory.value = globalProductCategories.value[0].id
    }
  } catch (error) {
    console.error('Failed to load product categories:', error)
  }
}


// 提供全局产品分类状态
provide('productCategories', globalProductCategories)

// 检测是否为移动设备
const isMobile = computed(() => windowWidth.value <= 600)

// 监听窗口大小变化
const handleResize = () => {
  windowWidth.value = window.innerWidth
}

onMounted(() => {
  // client-only: window/document specific logic
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth
    window.addEventListener('resize', handleResize)
    loadCategories()
  }

})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})


// 语言配置
const availableLanguages = [

  {
    code: 'en',
    name: 'English',
    flag: '/images/flags/gb.svg'
  },
  {
    code: 'fr',
    name: 'Français',
    flag: '/images/flags/fr.svg'
  },
  {
    code: 'ru',
    name: 'Русский',
    flag: '/images/flags/ru.svg'
  },
  {
    code: 'es',
    name: 'Español',
    flag: '/images/flags/es.svg'
  },
  {
    code: 'id',
    name: 'Bahasa Indonesia',
    flag: '/images/flags/id.svg'
  },
  {
    code: 'pt',
    name: 'Português',
    flag: '/images/flags/pt.svg'
  },
  {
    code: 'tr',
    name: 'Türk dili',
    flag: '/images/flags/tr.svg'
  }
]

// 在开发环境中显示中文选项，生产环境不显示
if (import.meta.env.DEV) {
  availableLanguages.push({
    code: 'zh',
    name: '中文',
    flag: '/images/flags/cn.svg'
  })
}

// 获取当前语言的国旗
const getLanguageFlag = (langCode: string) => {
  const lang = availableLanguages.find(l => l.code === langCode)
  return lang ? lang.flag : '/images/flags/gb.svg' // 默认英国国旗
}

// 切换语言
const switchLanguage = (langCode: string) => {
  locale.value = langCode
}

const menuItems = [
  {title: 'home', path: '/', icon: 'mdi-home'},
  {title: 'about', path: '/about', icon: 'mdi-information'},
  {title: 'products', path: '/products', icon: 'mdi-package-variant-closed'},
  {
    title: 'support', 
    path: '#', 
    icon: 'mdi-help-circle',
    hasSubmenu: true,
    submenu: [
      {title: 'userGuide', path: '/user-guide', icon: 'mdi-book-open-page-variant'},
      {title: 'faq', path: '/faq', icon: 'mdi-help-circle-outline'}
    ]
  },
  {title: 'contact', path: '/contact', icon: 'mdi-email'},
]

// 路由变化时关闭侧边栏
watch(
    () => route?.fullPath,
    (fullPath) => {
      if (fullPath) {
        drawer.value = false
      }
    }
)

// 导航到新页面
const navigateTo = (path: string) => {
  const categoryId = path.split('category=')[1]
  selectedCategory.value = categoryId
  router.push(path)
  drawer.value = false
}

onMounted(() => {
  // 初始化时检查URL参数（客户端）
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search)
    const langParam = urlParams.get('lang')
    if (langParam === 'zh') {
      locale.value = 'zh'
    }
  }
})
</script>

<template>
  <v-app>
    <!-- 顶部联系信息栏 -->
    <div class="top-header">
      <v-container class="top-header-container">
        <div class="top-header-left">
          <div class="top-header-item phone-item">
            <v-icon size="small" class="mr-1">mdi-phone</v-icon>
            <span>+86 15206211582</span>
          </div>
          <div class="top-header-item email-item">
            <v-icon size="small" class="mr-1">mdi-email</v-icon>
            <span>sales@fuhai-intl.com</span>
          </div>
        </div>

        <div class="top-header-right">
          <!-- 社交媒体图标 -->
          <div class="social-icons">
            <v-btn icon variant="text" class="social-icon" href="#" target="_blank">
              <v-icon>mdi-facebook</v-icon>
            </v-btn>
            <v-btn icon variant="text" class="social-icon" href="#" target="_blank">
              <v-icon>mdi-linkedin</v-icon>
            </v-btn>
            <v-btn icon variant="text" class="social-icon" href="#" target="_blank">
              <v-icon>mdi-instagram</v-icon>
            </v-btn>
            <v-btn icon variant="text" class="social-icon" href="#" target="_blank">
              <v-icon>mdi-twitter</v-icon>
            </v-btn>
          </div>

          <!-- 搜索框 -->
<!--          <div class="search-box">
            <v-text-field
                density="compact"
                variant="solo"
                hide-details
                placeholder="Please enter..."
                prepend-inner-icon="mdi-magnify"
                class="search-input"
                bg-color="white"
            ></v-text-field>
          </div>-->

          <!-- 语言选择器 -->
          <div class="language-switcher">
            <v-menu offset-y>
              <template v-slot:activator="{ props }">
                <v-btn
                    variant="text"
                    class="language-btn"
                    v-bind="props"
                >
                  <img :src="getLanguageFlag(locale)" class="flag-icon mr-2" alt="flag"/>
                  {{ t('language') }}
                  <v-icon size="small" class="ml-1">mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list density="compact" class="language-list">
                <v-list-item
                    v-for="lang in availableLanguages"
                    :key="lang.code"
                    @click="switchLanguage(lang.code)"
                    :class="{ 'active-language': locale === lang.code }"
                >
                  <template v-slot:prepend>
                    <img :src="lang.flag" class="flag-icon mr-2" alt="flag"/>
                  </template>
                  <v-list-item-title>{{ lang.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>

      </v-container>
    </div>

    <!-- 导航栏 -->
    <v-app-bar
        class="main-nav"
        :elevation="2"
        color="white"
        :height="60"
    >
      <div class="nav-container mx-auto">
        <!-- Logo -->
        <div class="logo-container">
          <router-link to="/" class="d-flex align-center text-decoration-none">
            <div class="logo-wrapper">
              <img src="/images/sol-logo.png" class="logo-img"/>
            </div>
            <div class="company-name ml-3">
              {{ t('brand.fullName') }}
            </div>
          </router-link>
        </div>

        <v-spacer></v-spacer>

        <!-- 桌面端导航菜单 -->
        <div class="d-none d-sm-flex align-center">
          <div v-for="item in menuItems" :key="item.title" style="position:relative;">
            <v-menu
                v-if="item.title === 'products' || (item.hasSubmenu && item.title === 'support')"
                :close-on-content-click="true"
                location="bottom"
                open-on-hover
                offset="1"
            >
              <template v-slot:activator="{ props }">
                <v-btn
                    v-bind="props"
                    :to="item.hasSubmenu ? undefined : item.path"
                    variant="text"
                    class="nav-btn font-weight-regular"
                    :ripple="false"
                    @click="item.hasSubmenu ? undefined : navigateTo(item.path)"
                >
                  {{ t(`nav.${item.title}`) }}
                </v-btn>
              </template>

              <!-- Products dropdown -->
              <v-card
                  v-if="item.title === 'products'"
                  width="200"
                  class="product-categories-card"
                  elevation="0"
                  border="1"
              >
                <v-list
                    class="product-categories-list"
                >
                  <v-list-item
                      v-for="category in globalProductCategories"
                      :key="category.id"
                      :to="`/products?category=${category.id}`"
                      @click="navigateTo(`/products?category=${category.id}`)"
                      class="product-category-item"
                      active-class="active-category"
                      :active="selectedCategory === category.id"
                  >
                    <v-list-item-title class="text-subtitle-2">
                      {{ category.name[locale] }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card>

              <!-- Support dropdown -->
              <v-card
                  v-if="item.title === 'support'"
                  width="200"
                  class="product-categories-card"
                  elevation="0"
                  border="1"
              >
                <v-list
                    class="product-categories-list"
                >
                  <v-list-item
                      v-for="subItem in item.submenu"
                      :key="subItem.title"
                      :to="subItem.path"
                      @click="navigateTo(subItem.path)"
                      class="product-category-item"
                  >
                    <template v-slot:prepend>
                      <v-icon size="small" color="primary" class="mr-2">{{ subItem.icon }}</v-icon>
                    </template>
                    <v-list-item-title class="text-subtitle-2">
                      {{ t(`nav.${subItem.title}`) }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>

            <v-btn
                v-else
                :to="item.path"
                variant="text"
                class="nav-btn font-weight-regular"
                :ripple="false"
                @click="navigateTo(item.path)"
            >
              {{ t(`nav.${item.title}`) }}
            </v-btn>
          </div>
        </div>

        <!-- 移动端菜单按钮 -->
        <v-app-bar-nav-icon
            @click="drawer = !drawer"
            class="d-flex d-sm-none"
            color="primary"
        ></v-app-bar-nav-icon>
      </div>
    </v-app-bar>


    <!-- 移动端侧边导航 -->
    <v-navigation-drawer
        v-model="drawer"
        temporary
        location="right"
        @update:model-value="val => !val && (drawer = false)"
    >
      <v-list>
        <template v-for="item in menuItems" :key="item.title">
          <!-- Regular menu item -->
          <v-list-item
              v-if="!item.hasSubmenu"
              :to="item.path"
              :ripple="true"
              class="mb-2"
              color="primary"
              @click="navigateTo(item.path)"
          >
            <template v-slot:prepend>
              <v-icon color="primary">{{ item.icon }}</v-icon>
            </template>
            <v-list-item-title>
              {{ t(`nav.${item.title}`) }}
            </v-list-item-title>
          </v-list-item>
          
          <!-- Menu item with submenu -->
          <v-list-group v-else :value="false">
            <template v-slot:activator="{ props }">
              <v-list-item
                v-bind="props"
                :ripple="true"
                color="primary"
              >
                <template v-slot:prepend>
                  <v-icon color="primary">{{ item.icon }}</v-icon>
                </template>
                <v-list-item-title>{{ t(`nav.${item.title}`) }}</v-list-item-title>
              </v-list-item>
            </template>
            
            <v-list-item
              v-for="subItem in item.submenu"
              :key="subItem.title"
              :to="subItem.path"
              :ripple="true"
              class="pl-4"
              color="primary"
              @click="navigateTo(subItem.path)"
            >
              <template v-slot:prepend>
                <v-icon color="primary" size="small">{{ subItem.icon }}</v-icon>
              </template>
              <v-list-item-title class="text-subtitle-2">
                {{ t(`nav.${subItem.title}`) }}
              </v-list-item-title>
            </v-list-item>
          </v-list-group>
        </template>

        <!-- 语言切换器 - 移动端抽屉菜单 -->
        <v-divider class="my-4"></v-divider>
        <v-list-group>
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              :ripple="true"
              color="primary"
            >
              <template v-slot:prepend>
                <img :src="getLanguageFlag(locale)" class="flag-icon-drawer mr-3" alt="flag"/>
              </template>
              <v-list-item-title>{{ t('language') }}</v-list-item-title>
            </v-list-item>
          </template>
          
          <v-list-item
            v-for="lang in availableLanguages"
            :key="lang.code"
            :ripple="true"
            class="pl-4"
            color="primary"
            @click="switchLanguage(lang.code)"
            :class="{ 'active-language': locale === lang.code }"
          >
            <template v-slot:prepend>
              <img :src="lang.flag" class="flag-icon-drawer mr-3" alt="flag"/>
            </template>
            <v-list-item-title class="text-subtitle-2">
              {{ lang.name }}
            </v-list-item-title>
          </v-list-item>
        </v-list-group>
      </v-list>
    </v-navigation-drawer>

    <!-- 主要内容区域 -->
    <v-main class="content-area">
      <router-view v-slot="{ Component, route }">
        <template v-if="Component">
          <component :is="Component" :key="route.path"/>
        </template>
        <template v-else>
          <v-container>
            <v-alert 
              type="error" 
              title="Page Not Found" 
              text="The requested page could not be loaded. Please try again or contact support."
            ></v-alert>
          </v-container>
        </template>
      </router-view>
    </v-main>

    <!-- 页脚 -->
    <Footer/>
  </v-app>
</template>

<style scoped>


/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgb(var(--v-theme-secondary));
}

/* 顶部联系信息栏 */
.top-header {
  background-color: rgb(var(--v-theme-primary)); /* 使用主题色 */
  color: white;
  height: 54px;
  display: flex;
  align-items: center;
  font-size: 14px;
  position: sticky;
  left: 0;
  right: 0;
  top: 0;
  z-index: 1006;
}

.top-header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  max-width: 1400px;
}

.top-header-left {
  display: flex;
  align-items: center;
}

.top-header-right {
  display: flex;
  align-items: center;
}

.top-header-item {
  display: flex;
  align-items: center;
  margin-right: 20px;
  color: white;
}

.phone-item, .email-item {
  display: flex;
  align-items: center;
}

/* 社交媒体图标 */
.social-icons {
  display: flex;
  align-items: center;
}

.social-icon {
  color: white !important;
  margin-left: 5px;
  width: 30px !important;
  height: 30px !important;
}

/* 搜索框 */
.search-box {
  margin: 0 15px;
}

.search-input {
  max-width: 200px;
  font-size: 14px;
}

.search-input :deep(.v-field__input) {
  min-height: 30px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* 语言选择器 */
.language-switcher {
  margin-left: 10px;
}

.language-btn {
  text-transform: none !important;
  letter-spacing: normal !important;
  font-weight: 400;
  color: white !important;
  height: 54px !important;
}

.flag-icon {
  width: 20px;
  height: 15px;
  object-fit: cover;
  border-radius: 2px;
}

.flag-icon-drawer {
  width: 24px;
  height: 18px;
  object-fit: cover;
  border-radius: 3px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.language-list {
  min-width: 200px;
}

.active-language {
  background-color: rgba(var(--v-theme-primary), 0.1);
}

.active-language-item {
  background-color: rgba(var(--v-theme-primary), 0.1);
}

/* 导航栏样式 */
.main-nav {
  top: 54px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.nav-container {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 1400px;
  height: 100%;
  padding: 0 !important;
}

/* Logo样式 */
.logo-container {
  padding-left: 20px;
}

.logo-wrapper {
  border-radius: 0;
  position: relative;
}

.logo-wrapper .logo-img {
  height: 40px;
  min-width: 40px;
}

.company-name {
  font-size: 16px;
  color: #333;
  font-weight: 500;
}

/* 导航按钮样式 */
.nav-btn {
  font-size: 16px !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  padding: 0 20px !important;
  height: 60px !important;
  border-radius: 0 !important;
  color: rgba(0, 0, 0, 0.87) !important;
  position: relative;
  transition: color 0.3s ease;
}

/* 导航按钮下划线效果 */
.nav-btn::after {
  content: '';
  position: absolute;
  bottom: 10px;
  left: 50%;
  width: 0;
  height: 2px;
  background-color: rgb(var(--v-theme-primary));
  transition: all 0.3s ease;
  transform: translateX(-50%);
}

.nav-btn:hover::after {
  width: 70%;
}

/* 激活状态的导航按钮 */
.nav-btn.router-link-active {
  color: rgb(var(--v-theme-primary)) !important;
  font-weight: 500 !important;
}

.nav-btn.router-link-active::after {
  width: 70%;
}

/* 内容区域 */
.content-area {
  overflow-y: auto;
}

/* 移动端样式 */
@media (max-width: 600px) {
  .main-nav {
    height: 60px !important;
  }

  .nav-btn {
    height: 60px !important;
  }

  .search-box {
    display: none;
  }

  .top-header-left {
    flex-direction: column;
    align-items: flex-start;
  }

  .top-header-right {
    display: none;
  }

  .phone-item, .email-item {
    margin-bottom: 4px;
  }

  .company-name {
    font-size: 12px;
  }

  .logo-wrapper {
    width: 40px;
    height: 40px; display: none !important;
  }

 
}

/* 产品分类下拉菜单样式 */
.products-dropdown {
  position: absolute;
  top: 100%; /* 直接定位在产品菜单下方 */
  left: 0;
  right: 0;
  width: 100%;
  background-color: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 10;
  padding: 20px 0;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.products-list {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 10px;
  max-width: 1400px;
  margin: 0 auto;
}

.products-list-item {
  padding: 10px;
  border-radius: 8px;
  transition: background-color 0.3s ease;
}

.products-list-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.1);
}

/* 响应式调整 */
@media (max-width: 600px) {
  .products-dropdown {
    display: none; /* 移动端不显示 */
  }
}

:deep(.v-toolbar__content) {
  overflow: auto !important;
}

/* 产品分类菜单样式 */
:deep(.v-menu__content) {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
  border-radius: 8px !important;
  overflow: hidden;
}

:deep(.v-list-item) {
  padding: 10px 16px !important;
  transition: background-color 0.3s ease;
}

:deep(.v-list-item:hover) {
  background-color: rgba(var(--v-theme-primary), 0.1) !important;
}

.product-categories-card {
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
}

.product-categories-list {
  background-color: white;
}

.product-category-item {
  transition: all 0.3s ease;
  margin-bottom: 4px;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  background-color: white !important;
}

.product-category-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.active-category {
  background-color: rgba(var(--v-theme-primary), 0.1) !important;
  color: rgb(var(--v-theme-primary)) !important;
  font-weight: 600;
  position: relative;
}


</style>
